import * as THREE from 'three'
import { TimelineLite, TweenLite } from 'gsap'

import Terrain from '../Terrain.js'
import ScanModel from '../ScanModel.js'
import TankInteraction from '../Interactions/Tank.js'

export default class Validate
{
    constructor(_options)
    {
        this.$eventHub = _options.$eventHub
        this.camera = _options.camera
        this.loader = _options.loader
        this.time = _options.time
        this.sizes = _options.sizes
        this.ui = _options.ui
        this.mobile = _options.mobile
        this.cursor = _options.cursor
        this.position = _options.position
        this.terrainGeometry = _options.terrainGeometry

        this.name = 'validate'
        this.container = new THREE.Object3D()
        this.container.position.copy(this.position)

        this.setTerrain()
    }

    /**
     * Set terrain
     */
    setTerrain()
    {
        this.terrain = new Terrain({
            mobile: this.mobile,
            time: this.time,
            loader: this.loader,
            ui: this.ui,
            geometry: this.terrainGeometry,
            texture: this.loader.resources.terrainTexture
        })

        this.terrain.plane.material.uniforms.uElevationNoiseMultiplier.value = 0.07
        this.terrain.plane.material.uniforms.uAlpha.value = 0

        this.container.add(this.terrain.container)
    }

    /**
     * Set contents
     */
    setContents()
    {
        this.setModels()
        this.setInteraction()
        this.setSliderTimeline()
        this.setFocusTimeline()
    }

    /**
     * Set models
     */
    setModels()
    {
        /**
         * Room
         */
        this.room = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.factoryRoomModelObj.geometry,
            revealPosition: { x: -57.66, y: 1.24, z: 18.7 },
            color: { x: 0.1, y: 0.1, z: 0.1 }
        })
        this.room.container.rotation.y = Math.PI * 0.5
        this.room.container.scale.x = 0.1
        this.room.container.scale.y = 0.1
        this.room.container.scale.z = 0.1
        this.room.container.position.x = - 0.9
        this.room.container.position.z = - 5.1
        this.container.add(this.room.container)

        /**
         * Tank
         */
        this.tank = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.tankModelObj.geometry,
            revealPosition: { x: 5.61, y: 1.24, z: -0.94 },
            color: { x: 0.14, y: 0.14, z: 0.14 }
        })
        this.tank.container.scale.x = 0.1
        this.tank.container.scale.y = 0.1
        this.tank.container.scale.z = 0.1
        this.tank.container.position.x = - 1.25
        this.tank.container.position.y = 0.16
        this.tank.container.position.z = - 5
        this.tank.container.rotation.y = - 0.87
        this.container.add(this.tank.container)

        /**
         * Tablet
         */
        this.tablet = {}

        this.tablet.rotation = {}
        this.tablet.rotation.y = - Math.PI * 0.79
        this.tablet.rotation.z = Math.PI * 0.5

        // Container
        this.tablet.container = new THREE.Object3D()
        this.tablet.container.scale.x = 0.06
        this.tablet.container.scale.y = 0.06
        this.tablet.container.scale.z = 0.06
        this.tablet.container.position.x = - 0.64
        this.tablet.container.position.y = 0.13
        this.tablet.container.position.z = - 5.5
        this.tablet.container.rotation.y = this.tablet.rotation.y
        this.tablet.container.rotation.z = this.tablet.rotation.z
        this.container.add(this.tablet.container)

        if(this.camera.type === 'guided')
        {
            this.time.on('tick', () =>
            {
                const panX = this.camera.pan.value.x / this.camera.pan.amplitude.x
                const panY = this.camera.pan.value.y / this.camera.pan.amplitude.y

                this.tablet.container.rotation.y = this.tablet.rotation.y - panX * (this.mobile ? 0.25 : 1)
                this.tablet.container.rotation.z = this.tablet.rotation.z + panY * (this.mobile ? 0.25 : 1)
            })
        }

        // Object
        this.tablet.object = new ScanModel({
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            geometry: this.loader.resources.tabletModelObj.geometry,
            revealPosition: { x: 1.24, y: 0.15, z: -3.12 },
            // color: { x: 0.3, y: 0.25, z: 0.1 }
            color: { x: 0.18, y: 0.18, z: 0.18 }
        })
        this.tablet.container.add(this.tablet.object.container)

        // // UI
        // if(this.ui)
        // {
        //     // Folder
        //     const folder = this.ui.addFolder('tank object')
        //     folder.open()

        //     // Tweaks
        //     folder
        //         .add(this.tablet.object.material.uniforms.uRevealPosition.value, 'x', - 100, 100, 0.01)
        //         .name('uRevealPosition x')

        //     folder
        //         .add(this.tablet.object.material.uniforms.uRevealPosition.value, 'y', - 100, 100, 0.01)
        //         .name('uRevealPosition y')

        //     folder
        //         .add(this.tablet.object.material.uniforms.uRevealPosition.value, 'z', - 100, 100, 0.01)
        //         .name('uRevealPosition z')

        //     folder
        //         .add(this.tablet.object.material.uniforms.uRevealDistance, 'value', 0, 100, 0.01)
        //         .name('uRevealDistance')
        // }

        this.tablet.screenBackground = new THREE.Mesh(
            this.loader.resources.tabletScreenModelObj.children[0].geometry,
            new THREE.MeshBasicMaterial({
                transparent: true,
                opacity: 0.65,
                color: 0x05070f
            })
        )
        this.tablet.screenBackground.position.y = 0
        this.tablet.container.add(this.tablet.screenBackground)

        // Screen
        this.screens = {}
        this.screens.options = [
            { texture: this.loader.resources.validateTabletScreen1Texture, color: null },
            { texture: this.loader.resources.validateTabletScreen2Texture, color: 0xffea00 },
            { texture: this.loader.resources.validateTabletScreen3Texture, color: 0xffea00 },
            { texture: this.loader.resources.validateTabletScreen4Texture, color: 0xffea00 },
            { texture: this.loader.resources.validateTabletScreen5Texture, color: 0xffea00 },
            { texture: this.loader.resources.validateTabletScreen6Texture, color: 0xffea00 }
        ]

        this.screens.items = []

        let i = 0
        for(const _options of this.screens.options)
        {
            const screenTexture = new THREE.Texture(_options.texture)
            // screenTexture.minFilter = THREE.NearestFilter
            // screenTexture.minFilter = THREE.NearestMipMapNearestFilter
            // screenTexture.minFilter = THREE.NearestMipMapLinearFilter
            // screenTexture.minFilter = THREE.LinearFilter
            // screenTexture.minFilter = THREE.LinearMipMapNearestFilter
            screenTexture.minFilter = THREE.LinearMipMapLinearFilter
            screenTexture.needsUpdate = true

            const mesh = new THREE.Mesh(
                this.loader.resources.tabletScreenModelObj.children[0].geometry,
                new THREE.MeshBasicMaterial({
                    transparent: true,
                    blending: THREE.AdditiveBlending,
                    opacity: 0.2,
                    map: screenTexture,
                    color: _options.color
                })
            )
            mesh.position.y = (i + 1) * 0.1
            this.tablet.container.add(mesh)

            this.screens.items.push(mesh)

            i++
        }
    }

    /**
     * Set interaction
     */
    setInteraction()
    {
        this.interaction = new TankInteraction({
            $eventHub: this.$eventHub,
            mobile: this.mobile,
            camera: this.camera,
            sizes: this.sizes,
            time: this.time,
            cursor: this.cursor,
            ui: this.ui,
            loader: this.loader
        })

        this.interaction.container.position.x = 0.14
        this.interaction.container.position.z = - 5.9
        this.interaction.container.position.y = - 3.34

        this.interaction.container.rotation.order = 'YXZ'
        this.interaction.container.rotation.x = 0.64
        this.interaction.container.rotation.y = 0.52
        this.interaction.container.rotation.z = 0
    }

    /**
     * Show
     */
    show()
    {
        this.sliderTimeline.play()
    }

    /**
     * Hide
     */
    hide()
    {
        this.sliderTimeline.reverse()
    }

    /**
     * Set timeline
     */
    setSliderTimeline()
    {
        this.sliderTimeline = new TimelineLite({ paused: true })

        this.sliderTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uAlpha, 2, { value: 0 }, { value: 1 }), 1)
        this.sliderTimeline.add(TweenLite.fromTo(this.room.material.uniforms.uRevealDistance, 1.5, { value: 89 }, { value: 40 }), 2.2)
        this.sliderTimeline.add(TweenLite.fromTo(this.tank.material.uniforms.uRevealDistance, 1, { value: 8.6 }, { value: 2.5 }), 2.9)
        this.sliderTimeline.add(TweenLite.fromTo(this.tablet.object.material.uniforms.uRevealDistance, 0.6, { value: 4.81 }, { value: 1.5 }), 2.9)
        this.sliderTimeline.add(TweenLite.fromTo(this.tablet.screenBackground.material, 0.4, { opacity: 0 }, { opacity: 0.45 }), 3)

        let i = 0
        for(const _screen of this.screens.items)
        {
            this.sliderTimeline.add(TweenLite.fromTo(_screen.material, 0.6, { opacity: 0 }, { opacity: 1 }), 2.8 + i * 0.15)
            this.sliderTimeline.add(TweenLite.fromTo(_screen.position, 0.6, { y: (i + 1) * 0.1 + 0.3 }, { y: (i + 1) * 0.1 }), 2.8 + i * 0.15)

            i++
        }
    }

    /**
     * Set focus timeline
     */
    setFocusTimeline()
    {
        this.focusTimeline = new TimelineLite({ paused: true })

        this.focusTimeline.call(() =>
        {
            // Entering
            if(!this.focusTimeline._reversed)
            {
                this.container.add(this.interaction.container)
            }
            // Leaving
            else
            {
                this.container.remove(this.interaction.container)
            }
        }, null, this, 0)

        // Terrain
        this.focusTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uFocusColor.value, 2, { x: 1, y: 0.9, z: 0 }, { x: 0.2, y: 0.2, z: 0.12 }), 0)
        this.focusTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uFocusDistance.value, 2, { y: 0.4 }, { y: 0.4 }), 0)
        this.focusTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uFocusPosition.value, 2, { x: 0.1, y: 0, z: - 5.2 }, { x: 0.345, y: 0, z: - 5.168 }), 0)
        this.focusTimeline.add(TweenLite.fromTo(this.terrain.plane.material.uniforms.uColor.value, 2, { x: 0.12, y: 0.12, z: 0.12 }, { x: 0.06, y: 0.06, z: 0.06 }), 0)

        // Tank
        this.focusTimeline.call(() =>
        {
            if(!this.focusTimeline._reversed)
            {
                this.interaction.reset()
            }
        }, null, this, 0)
    }
}



// WEBPACK FOOTER //
// ./src/components/app-experience/Experience/Scenes/Validate.js